import { mapMaybe } from '@execonline-inc/collections';
import { parseIntM } from '@execonline-inc/numbers';
import { assertNever } from '@kofno/piper';
import { just, Maybe } from 'maybeasy';
import {
  fieldValue,
  findField,
  FormTemplate,
  SelectionOption,
} from '../../../../Resource/FormTemplates/Types';
import { EitherLearningCollectionStore } from '../../Types';
import { SourceIdentifier } from '../ChatbotPreview/Types';
import { ShowUnlicensedToggle } from '../CurrentCollectionStep/Builder/ExperienceCatalog/Types';
import { FileMetaData } from '../Edit/CurrentCollectionStep/Details/S3ImageUpload/Types';
import {
  browseAllFieldValue,
  Finishing,
  PreviewingUpdating,
  SavingDetails,
  Updating,
} from '../Edit/Store/Types';
import LearningDesignExperienceStore from '../Experience/Store';
import { Creating, LearningDesignSection, ProductCollectionSection } from '../Types';

type OutState = Creating | Updating | PreviewingUpdating | Finishing | SavingDetails;

interface LearningDesignExperienceOut {
  id: number | undefined;
  display_order: number | undefined;
}

interface OnDemandInstanceOut {
  id: number;
  enrollable: boolean;
}

interface ScheduledInstanceOut {
  id: number;
  enrollable: boolean;
}

interface ProgramFamilyExperienceOut extends LearningDesignExperienceOut {
  extension_ids: number[];
  on_demand_program_instance: OnDemandInstanceOut | undefined;
  scheduled_program_instances: ScheduledInstanceOut[] | undefined;
}

interface ScheduledProgramOut {
  id: number | undefined;
  scheduled_program_instances: ScheduledInstanceOut[] | undefined;
}

interface OrderedLearningDesignSection extends LearningDesignSection {
  displayOrder: number;
}

interface OrderedProductCollectionSection extends ProductCollectionSection {
  displayOrder: number;
}

type OrderedSection = OrderedLearningDesignSection | OrderedProductCollectionSection;

export interface LearningDesignOut {
  id: number;
  name: string;
  description: string;
  display_type: string;
  display_button_label: string;
  learning_design_state: string;
  display_order: number;
  experiences: LearningDesignExperienceOut[];
  browse_all: boolean;
}

export interface ProductCollectionOut {
  id: number;
  display_order: number;
}

interface fileDataType {
  obj_name: string;
  asset_type: string;
  file_name: string;
  extension: string;
}

interface LearningCollectionOut {
  learning_collection: {
    name: string;
    description: string;
    internal_name: string;
    browse_all: boolean;
    permission_type: string;
    product_license_id: string; // should this be a maybe since we have LC without pl now?
    client_groups: ReadonlyArray<number>;
    learning_designs: LearningDesignOut[];
    product_collections: ProductCollectionOut[];
    program_families: ProgramFamilyExperienceOut[];
    scheduled_programs: ScheduledProgramOut[];
    logo: fileDataType | [];
    warning_status: string;
    source_identifier: SourceIdentifier;
  };
}
const sectionsOut = (state: OutState): OrderedSection[] => {
  return state.sections.map((section, index) => ({
    ...section,
    displayOrder: index + 1,
  }));
};

const learningDesignsOut = (
  store: EitherLearningCollectionStore,
  state: OutState,
): LearningDesignOut[] => {
  return sectionsOut(state)
    .filter((section) => section.kind === 'learning-design-section')
    .map(({ displayOrder, resource, template }): LearningDesignOut => {
      return {
        id: resource.payload.id,
        name: fieldValue('name', 'string', template).getOrElseValue(resource.payload.name.text),
        description: fieldValue('description', 'string', template).getOrElseValue(
          resource.payload.description,
        ),
        display_type: fieldValue('display_type', 'string', template).getOrElseValue(
          resource.payload.displayType,
        ),
        display_button_label: fieldValue('display_button_label', 'string', template)
          .orElse(() => resource.payload.displayButtonLabel)
          .getOrElseValue(''),
        learning_design_state: 'build-your-own',
        display_order: displayOrder,
        experiences: store
          .learningDesignExperienceStores(resource)
          .map((experienceStore, index) => ({
            id: experienceStore.experienceResource.payload.id,
            display_order: index + 1,
          })),
        browse_all: browseAllFieldValue(template),
      };
    });
};

const productCollectionsOut = (state: OutState): ProductCollectionOut[] => {
  return sectionsOut(state)
    .filter((section) => section.kind === 'product-collection-section')
    .map(({ displayOrder, resource }): ProductCollectionOut => {
      return {
        id: resource.payload.id,
        display_order: displayOrder,
      };
    });
};

const permissionTypeOut = (state: OutState) => {
  switch (state.kind) {
    case 'creating':
      return 'private';
    case 'saving-details':
    case 'updating':
    case 'previewing-updating':
      return state.learningCollectionEditFormResource.payload.permissionType;
    case 'finishing':
      return fieldValue('permission_type', 'single-selection', state.template)
        .andThen((option) => just(option.value))
        .getOrElseValue('');
    default:
      assertNever(state);
  }
};

const getClientGroupValues = (options: readonly SelectionOption[]) =>
  options.map((option) => option.value);

const clientGroupsFinishing = (template: FormTemplate) =>
  fieldValue('permission_type', 'single-selection', template)
    .map((v) => {
      switch (v.value) {
        case 'client-groups':
          return fieldValue('client_groups', 'multiple-selection', template)
            .map(getClientGroupValues)
            .map(mapMaybe(parseIntM))
            .getOrElseValue([]);
        case 'private':
        case 'organizations':
        default:
          return [];
      }
    })
    .getOrElseValue([]);

const clientGroupsOut = (state: OutState) => {
  switch (state.kind) {
    case 'creating':
      return [];
    case 'saving-details':
    case 'updating':
    case 'previewing-updating':
      return state.learningCollectionEditFormResource.payload.clientGroups;
    case 'finishing':
      return clientGroupsFinishing(state.template);
    default:
      assertNever(state);
  }
};

const programFamiliesOut = (
  store: EitherLearningCollectionStore,
  state: OutState,
): ProgramFamilyExperienceOut[] =>
  findField('browse_all', 'boolean', state.template.fields)
    .map((browseAll) => {
      return browseAll.value === false || (browseAll.value === true && !store.browseAllCustomized)
        ? []
        : store.onDemandExperienceStores.map((experienceStore, index) => ({
            id: experienceStore.experienceResource.payload.id,
            display_order: (index += 1),
            extension_ids: store.availableExtensionIds,
            on_demand_program_instance:
              experienceStore.experienceResource.payload.availableOnDemandInstanceId
                .map<OnDemandInstanceOut | undefined>((id) => ({ id: id, enrollable: true }))
                .getOrElseValue(undefined),
            scheduled_program_instances: experienceStore.selectedIds.map<ScheduledInstanceOut>(
              (id) => ({
                id: id,
                enrollable: true,
              }),
            ),
          }));
    })
    .getOrElseValue([]);

const experienceStoreWithScheduledInstances = (experienceStore: LearningDesignExperienceStore) =>
  experienceStore.selectedIds.length > 0;

const equalsThisExperienceStore =
  (store: LearningDesignExperienceStore) =>
  (otherStore: LearningDesignExperienceStore): boolean =>
    store.experienceResource.payload.id === otherStore.experienceResource.payload.id;

const uniqueExperienceStores = (
  stores: ReadonlyArray<LearningDesignExperienceStore>,
): Array<LearningDesignExperienceStore> =>
  stores.filter((store, index) => stores.findIndex(equalsThisExperienceStore(store)) === index);

const scheduledProgramsOut = (state: OutState): ScheduledProgramOut[] => {
  const stores = state.sections
    .flatMap((section) => section.learningDesignExperiences.experienceStores)
    .filter(experienceStoreWithScheduledInstances);

  return uniqueExperienceStores(stores).map((experienceStore) => ({
    id: experienceStore.experienceResource.payload.id,
    scheduled_program_instances: experienceStore.selectedIds.map((id) => ({
      id: id,
      enrollable: true,
    })),
  }));
};

const learningCollectionEncoder = (
  store: EitherLearningCollectionStore,
  state: OutState,
  fileMetaData: Maybe<FileMetaData>,
  showWarning: ShowUnlicensedToggle,
): LearningCollectionOut => ({
  learning_collection: {
    name: fieldValue('name', 'string', state.template).getOrElseValue(''),
    description: fieldValue('description', 'string', state.template).getOrElseValue(''),
    internal_name: fieldValue('internal_name', 'string', state.template).getOrElseValue(''),
    browse_all: fieldValue('browse_all', 'boolean', state.template).getOrElseValue(false),
    product_license_id: store.selectedProductLicense
      .map((pl) => String(pl.payload.id))
      .getOrElseValue(''),
    permission_type: permissionTypeOut(state),
    client_groups: clientGroupsOut(state),
    learning_designs: learningDesignsOut(store, state),
    product_collections: productCollectionsOut(state),
    program_families: programFamiliesOut(store, state),
    scheduled_programs: scheduledProgramsOut(state),
    logo: fileMetaData
      .map<fileDataType | []>((data) => ({
        obj_name: data.objName,
        asset_type: data.assetType,
        file_name: data.fileName,
        extension: data.extension,
      }))
      .getOrElseValue([]),
    warning_status: showWarning,
    source_identifier: 'builder-v1',
  },
});

export default learningCollectionEncoder;
